import React, { useState, useRef } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { api, adminApi, fileUrl } from "@/lib/api";
import {
  Plus,
  Trash2,
  Upload,
  X,
  Save,
  Loader2,
  ImageIcon,
  GripVertical,
} from "lucide-react";

const emptyBlock = {
  title: "",
  body: "",
  image_url: null,
  sort_order: 0,
};

export default function AdminStory({ adminKey }) {
  const { t } = useLanguage();
  const [blocks, setBlocks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");
  const [initialized, setInitialized] = useState(false);
  const fileRef = useRef(null);

  const client = adminApi(adminKey);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get("/story");
      setBlocks(res.data || []);
    } finally {
      setLoading(false);
    }
  };

  if (!initialized) {
    setInitialized(true);
    load();
  }

  const startNew = () =>
    setEditing({ ...emptyBlock, sort_order: blocks.length + 1 });
  const startEdit = (b) => setEditing({ ...b });
  const cancel = () => {
    setEditing(null);
    setError("");
  };

  const update = (k) => (e) =>
    setEditing({ ...editing, [k]: e.target.value });

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await client.post("/uploads/image", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((prev) => ({ ...prev, image_url: res.data.url }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload misslyckades");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const save = async () => {
    setError("");
    try {
      const payload = {
        title: editing.title,
        body: editing.body || null,
        image_url: editing.image_url || null,
        sort_order: Number(editing.sort_order) || 0,
      };
      if (editing.id) {
        await client.patch(`/story/${editing.id}`, payload);
      } else {
        await client.post("/story", payload);
      }
      setEditing(null);
      await load();
    } catch (err) {
      setError(err?.response?.data?.detail || "Kunde inte spara");
    }
  };

  const remove = async (b) => {
    if (!window.confirm(t.admin.story.confirm_delete)) return;
    await client.delete(`/story/${b.id}`);
    await load();
  };

  return (
    <div data-testid="admin-story-panel">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <h2 className="font-display text-3xl md:text-4xl uppercase">
            {t.admin.story.title}
          </h2>
          <p className="mt-2 text-sm text-zinc-400 max-w-xl">
            {t.admin.story.lead}
          </p>
        </div>
        <button
          onClick={startNew}
          data-testid="admin-story-add-btn"
          className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-[0.2em] uppercase font-bold hover:bg-[#ff7a30] transition-colors"
        >
          <Plus size={14} /> {t.admin.story.add}
        </button>
      </div>

      {loading ? (
        <div className="text-zinc-500 font-mono text-sm">
          {t.common.loading}
        </div>
      ) : blocks.length === 0 ? (
        <div className="border border-zinc-800 bg-[#0d0d10] p-12 text-center text-zinc-500">
          {t.admin.story.empty}
        </div>
      ) : (
        <div className="space-y-3">
          {blocks.map((b, i) => (
            <article
              key={b.id}
              data-testid={`admin-story-row-${b.id}`}
              className="border border-zinc-800 bg-[#0d0d10] p-4 md:p-5 flex gap-4 items-start hover:bg-zinc-900/60 transition-colors"
            >
              <div className="text-zinc-700 mt-1">
                <GripVertical size={18} />
              </div>
              <div className="w-20 h-20 md:w-24 md:h-24 bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                {b.image_url ? (
                  <img
                    src={fileUrl(b.image_url)}
                    alt={b.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <ImageIcon size={24} className="text-zinc-700" />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-3 text-[10px] font-mono tracking-widest uppercase text-zinc-500">
                  Kapitel {String(i + 1).padStart(2, "0")}
                  <span className="text-zinc-700">·</span>
                  <span>Ordning {b.sort_order}</span>
                </div>
                <div className="font-display text-2xl md:text-3xl uppercase mt-1 truncate">
                  {b.title}
                </div>
                {b.body && (
                  <p className="text-sm text-zinc-400 mt-2 line-clamp-2 whitespace-pre-line">
                    {b.body}
                  </p>
                )}
              </div>
              <div className="flex gap-2 shrink-0">
                <button
                  onClick={() => startEdit(b)}
                  data-testid={`admin-story-edit-${b.id}`}
                  className="border border-zinc-700 text-white px-3 py-2 text-[11px] tracking-widest uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] transition-colors"
                >
                  Redigera
                </button>
                <button
                  onClick={() => remove(b)}
                  data-testid={`admin-story-delete-${b.id}`}
                  className="border border-zinc-700 text-zinc-500 px-3 py-2 hover:border-red-500 hover:text-red-400 transition-colors"
                  title={t.admin.delete}
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </article>
          ))}
        </div>
      )}

      {editing && (
        <div
          className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
          data-testid="admin-story-modal"
        >
          <div className="bg-[#0d0d10] border border-zinc-800 max-w-2xl w-full my-8">
            <div className="flex items-center justify-between p-6 border-b border-zinc-800">
              <div className="font-display text-2xl uppercase">
                {editing.id ? "Redigera block" : t.admin.story.add}
              </div>
              <button
                onClick={cancel}
                className="text-zinc-500 hover:text-white"
              >
                <X size={20} />
              </button>
            </div>

            <div className="p-6 grid grid-cols-1 gap-5">
              <div>
                <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500">
                  {t.admin.story.upload_image}
                </label>
                <div className="mt-3 flex gap-4 items-start">
                  <div className="w-32 h-24 bg-zinc-900 border border-zinc-800 flex items-center justify-center overflow-hidden shrink-0">
                    {editing.image_url ? (
                      <img
                        src={fileUrl(editing.image_url)}
                        alt="preview"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <ImageIcon size={28} className="text-zinc-700" />
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      ref={fileRef}
                      onChange={onUpload}
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      className="hidden"
                      data-testid="admin-story-file-input"
                    />
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      data-testid="admin-story-upload-btn"
                      className="inline-flex items-center gap-2 border border-zinc-700 text-white px-4 py-2 text-xs tracking-widest uppercase hover:border-[#ff5a00] hover:text-[#ff5a00] disabled:opacity-50"
                    >
                      {uploading ? (
                        <Loader2 size={14} className="animate-spin" />
                      ) : (
                        <Upload size={14} />
                      )}
                      {uploading ? "Laddar upp..." : t.admin.story.upload_image}
                    </button>
                    {editing.image_url && (
                      <button
                        type="button"
                        onClick={() =>
                          setEditing({ ...editing, image_url: null })
                        }
                        className="ml-2 text-xs text-zinc-500 hover:text-red-400"
                      >
                        {t.admin.story.remove_image}
                      </button>
                    )}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
                <div className="md:col-span-2">
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                    {t.admin.story.fields.title} *
                  </label>
                  <input
                    type="text"
                    data-testid="admin-story-title"
                    className="brut-input"
                    value={editing.title}
                    onChange={update("title")}
                    placeholder="t.ex. 1985 — Det började i Helsingborg"
                  />
                </div>
                <div>
                  <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                    {t.admin.story.fields.sort_order}
                  </label>
                  <input
                    type="number"
                    data-testid="admin-story-order"
                    className="brut-input"
                    value={editing.sort_order}
                    onChange={update("sort_order")}
                  />
                </div>
              </div>

              <div>
                <label className="text-xs font-mono tracking-[0.3em] uppercase text-zinc-500 block">
                  {t.admin.story.fields.body}
                </label>
                <textarea
                  data-testid="admin-story-body"
                  className="brut-input"
                  value={editing.body || ""}
                  onChange={update("body")}
                  rows={6}
                  placeholder="Berätta historien... Radbrytningar bevaras."
                />
              </div>
            </div>

            {error && (
              <div className="px-6 pb-2 text-sm text-red-400">{error}</div>
            )}

            <div className="p-6 border-t border-zinc-800 flex gap-3 justify-end">
              <button
                onClick={cancel}
                className="border border-zinc-700 text-white px-5 py-3 text-xs tracking-widest uppercase hover:border-zinc-500"
              >
                {t.admin.story.cancel}
              </button>
              <button
                onClick={save}
                data-testid="admin-story-save-btn"
                disabled={!editing.title}
                className="inline-flex items-center gap-2 bg-[#ff5a00] text-black px-5 py-3 text-xs tracking-widest uppercase font-bold hover:bg-[#ff7a30] disabled:opacity-50"
              >
                <Save size={14} /> {t.admin.story.save}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
